import { clsx } from "clsx";
import {
	Boxes,
	Building2,
	Check,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	Layers,
	Plus,
	Search,
} from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Badge } from "@/components/ui/badge";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useActiveCanteiro } from "@/features/canteiros/hooks/use-active-canteiro.ts";
import type { CanteiroStatus } from "@/features/canteiros/types.ts";
import { canteiroAccent, canteiroLocation } from "@/features/canteiros/utils.ts";
import { ObraFormDialog } from "@/features/stages/components/obra-form-dialog";
import { useActiveObra } from "@/features/stages/hooks/use-active-obra.ts";
import { type Obra, obraSwitchPath } from "@/features/stages/obra.ts";
import { usePermissions } from "@/hooks/use-permissions.ts";
import { useActiveContext } from "@/store/use-active-context.ts";

const CANTEIRO_STATUS_META: Record<
	CanteiroStatus,
	{ label: string; variant: "info" | "warning" | "secondary" }
> = {
	active: { label: "Em execução", variant: "info" },
	paused: { label: "Pausado", variant: "warning" },
	archived: { label: "Arquivado", variant: "secondary" },
};

export function ProjectSwitcher() {
	const [open, setOpen] = useState(false);
	const [drillCanteiroId, setDrillCanteiroId] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const [newObraOpen, setNewObraOpen] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();

	const {
		obra: activeObra,
		obras,
		isLoading: obrasLoading,
		isError: obrasError,
	} = useActiveObra();
	const {
		canteiro: activeCanteiro,
		canteiros,
		isLoading: canteirosLoading,
		isError: canteirosError,
	} = useActiveCanteiro();
	const setActiveCanteiro = useActiveContext((state) => state.setActiveCanteiro);
	const { has } = usePermissions();
	const canCreateObra = has("constructions:create");

	if (obrasLoading || canteirosLoading) {
		return (
			<div className="project-switcher__skeleton">
				<div className="project-switcher__skeleton-header">
					<Skeleton className="project-switcher__skeleton-overline" />
					<Skeleton className="project-switcher__skeleton-count" />
				</div>
				<div className="project-switcher__skeleton-active">
					<Skeleton className="project-switcher__skeleton-accent" />
					<Skeleton className="project-switcher__skeleton-name" />
				</div>
			</div>
		);
	}

	if (obrasError || canteirosError) {
		return (
			<div className="project-switcher__notice">
				<p className="project-switcher__notice-text">
					Não foi possível carregar o contexto.
				</p>
			</div>
		);
	}

	if (canteiros.length === 0) {
		return <EmptyState />;
	}

	const drillCanteiro = canteiros.find((c) => c.id === drillCanteiroId) ?? null;
	const drillObras = drillCanteiro
		? obras.filter((o) => o.canteiroId === drillCanteiro.id)
		: [];
	const query = search.trim().toLowerCase();
	const filteredObras = query
		? drillObras.filter((o) => o.name.toLowerCase().includes(query))
		: drillObras;

	function handleOpenChange(next: boolean) {
		if (next) {
			setDrillCanteiroId(activeCanteiro?.id ?? null);
			setSearch("");
		}
		setOpen(next);
	}

	function selectObra(obra: Obra) {
		setOpen(false);
		navigate(obraSwitchPath(obra, location));
	}

	function startCreateObra() {
		if (drillCanteiro) setActiveCanteiro(drillCanteiro.id);
		setOpen(false);
		setNewObraOpen(true);
	}

	function handleObraCreated(id: string) {
		navigate(`/${id}/etapas`);
	}

	return (
		<>
			<Popover open={open} onOpenChange={handleOpenChange} modal>
				<PopoverTrigger asChild>
					<button
						type="button"
						aria-label={`Obra ativa: ${activeObra?.name ?? "nenhuma"}. Trocar de obra.`}
						className="project-switcher__trigger"
					>
						<div className="project-switcher__header">
							<span className="project-switcher__overline">
								<Building2 />
								Obra ativa
							</span>
							<span className="project-switcher__count">
								{obras.length}
								<ChevronDown />
							</span>
						</div>

						<div className="project-switcher__active">
							<span
								className="obra-accent"
								data-accent={canteiroAccent(
									activeObra?.id ?? activeCanteiro?.id ?? "",
								)}
							/>
							<div className="project-switcher__active-info">
								<p className="project-switcher__active-name">
									{activeObra?.name ?? "Selecione uma obra"}
								</p>
							</div>
						</div>
					</button>
				</PopoverTrigger>

				<PopoverContent align="start" className="project-switcher__menu">
					{drillCanteiro ? (
						<>
							<div className="project-switcher__menu-header">
								<button
									type="button"
									className="project-switcher__back"
									aria-label="Voltar para canteiros"
									onClick={() => {
										setDrillCanteiroId(null);
										setSearch("");
									}}
								>
									<ChevronLeft />
								</button>
								<span className="project-switcher__menu-title">
									<Boxes />
									{drillCanteiro.name}
								</span>
							</div>

							{drillObras.length > 0 && (
								<div className="project-switcher__search">
									<Search className="project-switcher__search-icon" />
									<input
										type="search"
										value={search}
										onChange={(e) => setSearch(e.target.value)}
										placeholder="Buscar obra…"
										aria-label="Buscar obra"
										className="project-switcher__search-input"
									/>
								</div>
							)}

							<div className="project-switcher__list">
								{filteredObras.length === 0 ? (
									<p className="project-switcher__empty">
										{drillObras.length === 0
											? "Nenhuma obra neste canteiro."
											: "Nenhuma obra encontrada."}
									</p>
								) : (
									filteredObras.map((obra) => (
										<button
											key={obra.id}
											type="button"
											onClick={() => selectObra(obra)}
											className={clsx(
												"obra-list-item",
												obra.id === activeObra?.id && "is-active",
											)}
										>
											<span
												className="obra-accent"
												data-accent={canteiroAccent(obra.id)}
											/>
											<div className="obra-list-item__info">
												<p className="obra-list-item__name">{obra.name}</p>
											</div>
											{obra.id === activeObra?.id && (
												<Check className="project-switcher__check" />
											)}
										</button>
									))
								)}
							</div>

							{canCreateObra && (
								<>
									<Separator className="project-switcher__menu-separator" />
									<button
										type="button"
										className="project-switcher__add"
										onClick={startCreateObra}
									>
										<span className="project-switcher__add-icon">
											<Plus />
										</span>
										<div>
											<p className="project-switcher__add-title">Nova obra</p>
											<p className="project-switcher__add-sub">
												Cadastrar obra neste canteiro
											</p>
										</div>
									</button>
								</>
							)}
						</>
					) : (
						<>
							<p className="project-switcher__menu-overline">
								<Layers />
								Seus canteiros
							</p>

							<div className="project-switcher__list">
								{canteiros.map((canteiro) => {
									const meta = CANTEIRO_STATUS_META[canteiro.status];
									const loc = canteiroLocation(canteiro);
									return (
										<button
											key={canteiro.id}
											type="button"
											onClick={() => {
												setDrillCanteiroId(canteiro.id);
												setSearch("");
											}}
											className={clsx(
												"obra-list-item",
												canteiro.id === activeCanteiro?.id && "is-active",
											)}
										>
											<span
												className="obra-accent"
												data-accent={canteiroAccent(canteiro.id)}
											/>
											<div className="obra-list-item__info">
												<p className="obra-list-item__name">{canteiro.name}</p>
												<div className="obra-list-item__meta">
													<Badge variant={meta.variant}>{meta.label}</Badge>
													{loc && (
														<span className="obra-list-item__location">
															· {loc}
														</span>
													)}
												</div>
											</div>
											<ChevronRight className="project-switcher__drill" />
										</button>
									);
								})}
							</div>

							<Separator className="project-switcher__menu-separator" />

							<button
								type="button"
								className="project-switcher__add"
								onClick={() => {
									setOpen(false);
									navigate("/novo-canteiro");
								}}
							>
								<span className="project-switcher__add-icon">
									<Plus />
								</span>
								<div>
									<p className="project-switcher__add-title">Novo canteiro</p>
									<p className="project-switcher__add-sub">Cadastrar canteiro</p>
								</div>
							</button>
						</>
					)}
				</PopoverContent>
			</Popover>

			{canCreateObra && (
				<ObraFormDialog
					open={newObraOpen}
					onOpenChange={setNewObraOpen}
					onCreated={handleObraCreated}
				/>
			)}
		</>
	);
}

function EmptyState() {
	const navigate = useNavigate();
	return (
		<div className="project-switcher__notice">
			<span className="project-switcher__overline">
				<Building2 />
				Obra ativa
			</span>
			<button
				type="button"
				className="project-switcher__add project-switcher__add--spaced"
				onClick={() => navigate("/novo-canteiro")}
			>
				<span className="project-switcher__add-icon">
					<Plus />
				</span>
				<div>
					<p className="project-switcher__add-title">Novo canteiro</p>
					<p className="project-switcher__add-sub">
						Cadastre seu primeiro canteiro
					</p>
				</div>
			</button>
		</div>
	);
}
